import { useContext, useEffect, useRef } from 'react';
import { doc, setDoc, deleteDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { useFirestore } from '../helpers/firebaseContext';
import { MyUserDataContext } from '../contexts';
import { PresenceData, ProductType } from '../types';

const VALID_PRODUCT_TYPES: ProductType[] = ["customers", "orders", "services", "transactions"];
const UPDATE_INTERVAL = 30000; // Update every 30 seconds
const EXPIRY_TIME = 45; // Expire after 45 seconds

/**
 * Tracks user presence on specific product pages.
 */
export const PresenceTracker: React.FC = () => {
  const location = useLocation();
  const db = useFirestore();
  const { user } = useContext(MyUserDataContext);
  
  const userId = user?.uid;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const presenceDocIdRef = useRef<string | null>(null);

  useEffect(() => {
    const cleanup = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (presenceDocIdRef.current && db) {
        deleteDoc(doc(db, 'presence', presenceDocIdRef.current))
          .catch(error => console.log('Error deleting presence:', error));
        presenceDocIdRef.current = null;
      }
    };

    if (!userId || !user || !db) {
      cleanup();
      return;
    }

    // Parse the current path
    const pathParts = location.pathname.split('/').filter(Boolean);
    const productType = pathParts[0] as ProductType;
    let productId = pathParts.length > 1 ? pathParts[1] : null;

    // Skip if it's an add/create page
    if (productId && ['add', 'add_order', 'new', 'create'].includes(productId.toLowerCase())) {
      productId = null;
    }

    const isTrackablePage = productType && productId && VALID_PRODUCT_TYPES.includes(productType);

    if (isTrackablePage) {
      const presenceDocId = `${userId}_${productType}_${productId}`;
      
      // Clean up old presence if switching pages
      if (presenceDocIdRef.current && presenceDocIdRef.current !== presenceDocId) {
        if (db) {
          deleteDoc(doc(db, 'presence', presenceDocIdRef.current))
            .catch(error => console.log('Error deleting old presence:', error));
        }
      }

      const presenceDocRef = doc(db, 'presence', presenceDocId);

      const setPresence = async () => {
        try {
          const now = Timestamp.now();
          const expiresAt = new Timestamp(now.seconds + EXPIRY_TIME, now.nanoseconds);

          const presenceData: PresenceData = {
            id: productId!,
            type: productType,
            uid: userId,
            name: user.displayName || user.email?.split('@')[0] || 'Unknown User',
            email: user.email || '',
            timestamp: now,
            expiresAt: expiresAt,
            action: 'viewing',
            // Add a compound field for easier querying
            pageKey: `${productType}_${productId}`,
          };
          
          await setDoc(presenceDocRef, presenceData, { merge: true });
        } catch (error) {
          console.error('Error setting presence:', error);
        }
      };
      
      setPresence();
      intervalRef.current = setInterval(setPresence, UPDATE_INTERVAL);
      presenceDocIdRef.current = presenceDocId;

      // Also clean up on page visibility change
      const handleVisibilityChange = () => {
        if (document.hidden && presenceDocIdRef.current) {
          cleanup();
        } else if (!document.hidden && isTrackablePage) {
          setPresence();
          if (!intervalRef.current) {
            intervalRef.current = setInterval(setPresence, UPDATE_INTERVAL);
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        cleanup();
      };
    } else {
      cleanup();
    }

    return cleanup;
  }, [location.pathname, userId, db, user]);

  return null;
};